import {
  Button,
  DetailList,
  formatDate,
  formatDateTime,
  formatMoney,
  PageEmpty,
  PageError,
  PageHeader,
  PageLoading,
  TextButton,
} from "@majitap/ui";
import { Alert } from "@majitap/ui/arc/alert/alert";
import { Badge } from "@majitap/ui/arc/badge/badge";
import { Checkbox } from "@majitap/ui/arc/checkbox/checkbox";
import { Dialog, DialogContent } from "@majitap/ui/arc/dialog/dialog";
import { HoldToConfirm } from "@majitap/ui/arc/hold-to-confirm/hold-to-confirm";
import { Input } from "@majitap/ui/arc/input/input";
import { SortableDataTable } from "@majitap/ui/arc/sortable-data-table/sortable-data-table";
import { Textarea } from "@majitap/ui/arc/textarea/textarea";
import { useMutation, useQuery, useQueryClient, useSuspenseQuery } from "@tanstack/react-query";
import { Link, useNavigate, useParams } from "@tanstack/react-router";
import { Ban } from "lucide-react";
import { type ReactNode, useState } from "react";
import { errorOf } from "../../api";
import { businessActions } from "../../businesses";
import { useCompanyFormat } from "../../company";
import { useRecordCrumb } from "../../crumb";
import { customerActions } from "../../customers";
import { messageFor } from "../../error-copy";
import {
  INVOICE_SOURCE,
  type Invoice,
  type InvoiceDetail,
  invoiceActions,
  invoiceActionsFor,
  invoicePaymentsQuery,
  invoiceQuery,
  pdfUrl,
  statusBadge,
  taxLinksQuery,
} from "../../invoices";
import { meQuery } from "../../session";
import { RecordPaymentDialog } from "../orders/collect";
import { ConfirmDialog } from "../shared/confirm";
import { ActivityPanel } from "../shared/panels";
import { QuickbooksStatus } from "../shared/sync-status";
import styles from "./invoices.module.css";

/** How long Void must be held: it can't be undone. */
export const VOID_HOLD_MS = 2000;

function useRefreshInvoice(id: string) {
  const queryClient = useQueryClient();
  return async () => {
    await queryClient.invalidateQueries({ queryKey: ["invoice", id] });
    await queryClient.invalidateQueries({ queryKey: ["invoices"] });
    await queryClient.invalidateQueries({ queryKey: ["ageing"] });
    await queryClient.invalidateQueries({ queryKey: ["tax-links", id] });
    await queryClient.invalidateQueries({ queryKey: ["activity", "invoice", id] });
  };
}

/**
 * One invoice (P8-14, `Console / Invoice` in the design). Every amount is the API's decimal
 * string, formatted, never added up here.
 */
export function InvoicePage(): ReactNode {
  const { invoiceId } = useParams({ from: "/console/invoices/$invoiceId" });
  const { data: me } = useSuspenseQuery(meQuery);
  const permissions = me?.permissions ?? [];
  const invoice = useQuery(invoiceQuery(invoiceId));
  useRecordCrumb(invoice.data ? (invoice.data.invoice_number ?? "Draft") : undefined);
  if (invoice.status === "pending") return <PageLoading label="Loading the invoice" />;
  if (invoice.status === "error") {
    const error = errorOf(invoice.error);
    if (error.code === "INVOICE_NOT_FOUND")
      return (
        <PageEmpty title="No such invoice" description="It may be a draft that was deleted, or in another company." />
      );
    return <PageError message={messageFor(error)} onRetry={() => invoice.refetch()} />;
  }
  return <InvoiceView invoice={invoice.data} permissions={permissions} />;
}

/** The record, given the invoice: the page above, and a test, render it. */
export function InvoiceView({ invoice: i, permissions }: { invoice: InvoiceDetail; permissions: string[] }): ReactNode {
  const format = useCompanyFormat();
  const navigate = useNavigate();
  const refresh = useRefreshInvoice(i.id);
  const [dialog, setDialog] = useState<"issue" | "delete" | "send" | "record" | null>(null);
  const can = invoiceActionsFor(i.status);
  const [statusLabel, statusTone] = statusBadge(i);

  return (
    <>
      <PageHeader
        title={i.invoice_number ?? "Draft invoice"}
        description={[
          i.billee.name,
          i.issued_at ? `issued ${formatDate(i.issued_at, format)}` : "not issued",
          i.due_at ? `due ${formatDate(i.due_at, format)}` : null,
          i.payment_term?.name,
          INVOICE_SOURCE[i.source],
        ]
          .filter(Boolean)
          .join(" · ")}
        actions={
          <div className={styles.actions}>
            {can.remove ? (
              <Button variant="ghost" onClick={() => setDialog("delete")}>
                Delete draft
              </Button>
            ) : null}
            {can.edit ? (
              <Button
                variant="secondary"
                onClick={() => navigate({ to: "/invoices/$invoiceId/edit", params: { invoiceId: i.id } })}
              >
                Edit
              </Button>
            ) : null}
            {can.issue ? <Button onClick={() => setDialog("issue")}>Issue</Button> : null}
            {can.record && permissions.includes("payments.record") && i.amount_due !== "0.00" ? (
              <Button variant="secondary" onClick={() => setDialog("record")}>
                Record payment
              </Button>
            ) : null}
            {can.send ? (
              <Button variant="secondary" onClick={() => setDialog("send")}>
                {i.sent_at ? "Send again" : "Send"}
              </Button>
            ) : null}
          </div>
        }
      />
      {i.status === "void" ? (
        <Alert tone="info" title="This invoice is void">
          {[i.voided_at ? formatDateTime(i.voided_at, format) : null, i.void_reason].filter(Boolean).join(" · ")}
        </Alert>
      ) : null}
      <div className={styles.columns}>
        <div className={styles.main}>
          <section className={styles.section}>
            <div className={styles.cardHead}>
              <h2 className={styles.sectionTitle}>Lines</h2>
              <Badge tone={statusTone}>{statusLabel}</Badge>
            </div>
            <InvoiceLines invoice={i} format={format} />
            <InvoiceTotals invoice={i} format={format} />
            {i.notes ? <p className={styles.muted}>{i.notes}</p> : null}
            {i.order_id ? (
              <p className={styles.muted}>
                From{" "}
                <Link to="/orders/$orderId" params={{ orderId: i.order_id }} className={styles.link}>
                  the order
                </Link>
              </p>
            ) : null}
          </section>
          <Document invoice={i} />
          {permissions.includes("staff.manage") ? (
            <section className={styles.section}>
              <h2 className={styles.sectionTitle}>Activity</h2>
              <ActivityPanel targetType="invoice" id={i.id} label="What happened to this invoice" />
            </section>
          ) : null}
        </div>
        <aside className={styles.side}>
          <PaymentCard invoice={i} canSeePayments={permissions.includes("payments.record")} />
          {i.status !== "draft" ? (
            <TaxCard
              invoiceId={i.id}
              issued={i.status !== "void"}
              billee={i.billee}
              canSetPin={permissions.includes("customers.manage")}
            />
          ) : null}
          {i.status !== "draft" ? <QuickbooksStatus entityType="invoice" entityId={i.id} /> : null}
          {can.void ? <VoidInvoice invoiceId={i.id} /> : null}
        </aside>
      </div>
      {dialog === "issue" ? (
        <ConfirmDialog
          title="Issue this invoice?"
          description="It gets its number and due date, and can't change after. The customer owes it from now."
          action="Issue"
          run={() => invoiceActions.issue(i.id)}
          onDone={refresh}
          onClose={() => setDialog(null)}
        />
      ) : null}
      {dialog === "delete" ? (
        <ConfirmDialog
          title="Delete this draft?"
          description="Nothing was issued or owed. Its deliveries, if any, can be invoiced again."
          action="Delete draft"
          keep="Keep it"
          danger
          run={() => invoiceActions.remove(i.id)}
          onDone={async () => {
            await refresh();
            await navigate({ to: "/invoices" });
          }}
          onClose={() => setDialog(null)}
        />
      ) : null}
      {dialog === "send" ? <SendDialog invoice={i} onClose={() => setDialog(null)} /> : null}
      {dialog === "record" ? (
        <RecordPaymentDialog
          target={{ invoice_id: i.id }}
          number={i.invoice_number ?? "the invoice"}
          onClose={() => setDialog(null)}
        />
      ) : null}
    </>
  );
}

/** "Delivered 1 Nov 2026", or "2 deliveries · 1 Nov 2026 and 4 Nov 2026", or "5 deliveries · 1 Nov 2026 to 9 Nov 2026". */
export function deliveredText(dates: string[], format: Parameters<typeof formatDate>[1]): string | null {
  const days = [...new Set(dates.map((d) => formatDate(d, format)))];
  if (!days.length) return null;
  if (dates.length === 1) return `Delivered ${days[0]}`;
  const when =
    days.length > 3
      ? `${days[0]} to ${days.at(-1)}`
      : days.length > 1
        ? `${days.slice(0, -1).join(", ")} and ${days.at(-1)}`
        : days[0];
  return `${dates.length} deliveries · ${when}`;
}

/**
 * The lines by order (P8-72), as the PDF prints them: each order's number (to the order), when its
 * deliveries were delivered and where they went, then its lines, equal ones merged and named for
 * the product. The groups are the API's, amounts included: nothing is added up here. An invoice
 * with no orders (a manual one) is its lines as entered.
 */
export function InvoiceLines({
  invoice: i,
  format,
}: {
  invoice: Pick<InvoiceDetail, "lines" | "groups" | "currency">;
  format: Parameters<typeof formatMoney>[1] & Parameters<typeof formatDate>[1];
}): ReactNode {
  const money = (v: string) => formatMoney(v, { ...format, currency: i.currency });
  const columns = [
    { key: "description", label: "Description", sortable: false },
    { key: "quantity", label: "Qty", numeric: true, sortable: false },
    {
      key: "unit_price",
      label: "Unit price",
      numeric: true,
      sortable: false,
      render: (_v: unknown, l: { unit_price: string }) => money(l.unit_price),
    },
    {
      key: "tax_rate",
      label: "Tax",
      numeric: true,
      sortable: false,
      render: (_v: unknown, l: { tax_rate: string }) => `${l.tax_rate}%`,
    },
    {
      key: "amount",
      label: "Amount",
      numeric: true,
      sortable: false,
      render: (_v: unknown, l: { amount: string }) => money(l.amount),
    },
  ] as const;
  if (!i.groups.some((g) => g.order))
    return (
      <SortableDataTable
        caption="Lines"
        rowKey="id"
        rows={i.lines}
        itemName={{ one: "line", other: "lines" }}
        columns={[...columns]}
      />
    );
  return i.groups.map((g, n) => (
    <div key={g.order?.id ?? `none-${n}`} className={styles.orderGroup}>
      {g.order ? (
        <div>
          <Link to="/orders/$orderId" params={{ orderId: g.order.id }} className={styles.link}>
            {g.order.order_number}
          </Link>
          <p className={styles.muted}>
            {[
              deliveredText(g.delivered_at, format),
              g.location ? [g.location.label, g.location.address].filter(Boolean).join(", ") : null,
            ]
              .filter(Boolean)
              .join(" · ")}
          </p>
        </div>
      ) : null}
      <SortableDataTable
        caption={g.order ? `Lines of ${g.order.order_number}` : "Other lines"}
        rowKey={(l) => `${l.description}|${l.unit_price}|${l.tax_rate}`}
        rows={g.lines}
        itemName={{ one: "line", other: "lines" }}
        columns={[...columns]}
      />
    </div>
  ));
}

/**
 * Subtotal, tax and total exactly as the API sent them: formatted, never added up here (the API
 * rounds per line, and a sum in the browser could disagree with the invoice and the ledger).
 */
export function InvoiceTotals({
  invoice: i,
  format,
}: {
  invoice: Pick<Invoice, "subtotal_amount" | "tax_amount" | "total_amount" | "currency">;
  format: Parameters<typeof formatMoney>[1];
}) {
  const money = (v: string) => formatMoney(v, { ...format, currency: i.currency });
  return (
    <div className={styles.totals}>
      <span className={styles.totalRow}>
        Subtotal <span className={styles.amount}>{money(i.subtotal_amount)}</span>
      </span>
      <span className={styles.totalRow}>
        Tax <span className={styles.amount}>{money(i.tax_amount)}</span>
      </span>
      <span className={styles.totalRow}>
        <strong>Total</strong> <strong className={styles.amount}>{money(i.total_amount)}</strong>
      </span>
    </div>
  );
}

function PaymentCard({ invoice: i, canSeePayments }: { invoice: Invoice; canSeePayments: boolean }) {
  const format = useCompanyFormat();
  const payments = useQuery({ ...invoicePaymentsQuery(i.id), enabled: canSeePayments && i.status !== "draft" });
  const money = (v: string) => formatMoney(v, { ...format, currency: i.currency });
  return (
    <section className={styles.card} aria-label="Payment">
      <h2 className={styles.sectionTitle}>Payment</h2>
      <div className={styles.totals}>
        <span className={styles.totalRow}>
          Total <span className={styles.amount}>{money(i.total_amount)}</span>
        </span>
        <span className={styles.totalRow}>
          Paid <span className={styles.amount}>{money(i.amount_paid)}</span>
        </span>
        <span className={styles.totalRow}>
          <strong>Owed</strong> <strong className={styles.amount}>{money(i.amount_due)}</strong>
        </span>
      </div>
      {payments.data?.length ? (
        <ul className={styles.section}>
          {payments.data.map((p) => (
            <li key={p.id} className={styles.muted}>
              <Link to="/payments/$paymentId" params={{ paymentId: p.id }} className={styles.link}>
                {p.reference}
              </Link>{" "}
              ·{" "}
              {[
                formatDate(p.succeeded_at ?? p.created_at, format),
                p.method.replace("_", " "),
                p.receipt_number,
                money(p.amount),
                p.status === "succeeded" ? null : p.status,
              ]
                .filter(Boolean)
                .join(" · ")}
            </li>
          ))}
        </ul>
      ) : null}
      {i.pay_url && i.amount_due !== "0.00" ? <PayLink url={i.pay_url} /> : null}
    </section>
  );
}

/** The public pay link (M-Pesa, no sign-in), also in the SMS and on the PDF: staff share it by WhatsApp or phone. */
function PayLink({ url }: { url: string }) {
  const [copied, setCopied] = useState(false);
  return (
    <div className={styles.section}>
      <span className={styles.muted}>Pay link: the customer pays by M-Pesa without signing in.</span>
      <span className={styles.actions}>
        <a href={url} className={`${styles.link} ${styles.payUrl}`} target="_blank" rel="noreferrer">
          {url}
        </a>
        <TextButton
          onClick={() =>
            void navigator.clipboard?.writeText(url).then(
              () => setCopied(true),
              () => setCopied(false),
            )
          }
        >
          {copied ? "Copied" : "Copy"}
        </TextButton>
      </span>
    </div>
  );
}

const TAX_STATE: Record<string, [string, "neutral" | "success" | "warning" | "danger"]> = {
  pending: ["Waiting", "warning"],
  synced: ["Fiscalised", "success"],
  failed: ["Refused", "danger"],
  skipped: ["Not sent", "neutral"],
};

/** KRA eTIMS: shown when the company fiscalises (it has a link for this invoice). */
export function TaxCard({
  invoiceId,
  issued,
  billee,
  canSetPin,
}: {
  invoiceId: string;
  issued: boolean;
  billee: InvoiceDetail["billee"];
  canSetPin: boolean;
}) {
  const refresh = useRefreshInvoice(invoiceId);
  const { data: me } = useSuspenseQuery(meQuery);
  const links = useQuery(taxLinksQuery(invoiceId));
  const resubmit = useMutation({
    mutationFn: (document: "sale" | "credit_note") => invoiceActions.resubmitTax(invoiceId, document),
    onSuccess: refresh,
  });
  const rows = [
    ["sale", "Sale", links.data?.sale],
    ["credit_note", "Credit note", links.data?.creditNote],
  ] as const;
  // eTIMS is Kenya's: elsewhere an invoice that was never sent isn't missing anything
  const kenyan = me?.active_company?.country === "KEN"; // ISO 3166-1 alpha-3, as the API sends it
  if (!links.data) return null;
  const unsent = !links.data.sale && !links.data.creditNote;
  if (unsent && !(kenyan && issued)) return null;
  const notFiscalised = links.data.sale?.sync_status !== "synced";
  return (
    <section className={styles.card} aria-label="KRA eTIMS">
      <h2 className={styles.sectionTitle}>KRA eTIMS</h2>
      {kenyan && issued && notFiscalised && !billee.tax_pin ? (
        <KraPinForm invoiceId={invoiceId} billee={billee} canSetPin={canSetPin} />
      ) : null}
      {unsent ? (
        <div className={styles.section}>
          <p className={styles.muted}>
            Not sent to KRA. An invoice is sent when it's issued if the account has a KRA PIN, and eTIMS was on.
          </p>
          <div>
            <TextButton disabled={resubmit.isPending} onClick={() => resubmit.mutate("sale")}>
              Send to eTIMS
            </TextButton>
          </div>
        </div>
      ) : null}
      {rows.map(([document, label, link]) =>
        link ? (
          <div key={document} className={styles.section}>
            <div className={styles.cardHead}>
              <span>{label}</span>
              <Badge tone={(TAX_STATE[link.sync_status] ?? TAX_STATE.pending)[1]}>
                {(TAX_STATE[link.sync_status] ?? TAX_STATE.pending)[0]}
              </Badge>
            </div>
            {link.state && link.sync_status === "pending" ? (
              <p className={styles.muted}>{TAX_STEP[link.state] ?? link.state}</p>
            ) : null}
            {link.last_error ? <p className={styles.muted}>{link.last_error}</p> : null}
            {link.receipt ? <TaxReceipt receipt={link.receipt} /> : null}
            {link.sync_status !== "synced" ? (
              <div>
                <TextButton disabled={resubmit.isPending} onClick={() => resubmit.mutate(document)}>
                  {link.sync_status === "skipped" ? "Send to eTIMS" : "Resubmit"}
                </TextButton>
              </div>
            ) : null}
          </div>
        ) : null,
      )}
      {resubmit.isSuccess ? <p className={styles.muted}>Sent. The status updates when KRA answers.</p> : null}
      {resubmit.error ? (
        <Alert tone="danger" title="Not sent">
          {messageFor(errorOf(resubmit.error))}
        </Alert>
      ) : null}
    </section>
  );
}

/** A KRA PIN: a letter, 9 digits and a letter (A123456789B). */
const KRA_PIN = /^[A-Z]\d{9}[A-Z]$/;

/**
 * The account billed has no KRA PIN: on issue, eTIMS only sends invoices with one. Staff who
 * manage customers add it to the customer or the business here, then send the invoice.
 */
function KraPinForm({
  invoiceId,
  billee,
  canSetPin,
}: {
  invoiceId: string;
  billee: InvoiceDetail["billee"];
  canSetPin: boolean;
}) {
  const refresh = useRefreshInvoice(invoiceId);
  const queryClient = useQueryClient();
  const [pin, setPin] = useState("");
  const typed = pin.trim().toUpperCase();
  const save = useMutation({
    mutationFn: async () => {
      if (billee.kind === "business") await businessActions.update(billee.id, { tax_pin: typed });
      else await customerActions.update(billee.id, { tax_pin: typed });
    },
    onSuccess: async () => {
      setPin("");
      await queryClient.invalidateQueries({ queryKey: [billee.kind === "business" ? "businesses" : "customers"] });
      await refresh();
    },
  });
  const who = billee.kind === "business" ? "business" : "customer";
  if (!canSetPin) {
    return (
      <p className={styles.muted}>
        {billee.name} has no KRA PIN, so this invoice isn't sent to KRA on its own. Someone who manages customers can
        add it.
      </p>
    );
  }
  return (
    <div className={styles.section}>
      <p className={styles.muted}>
        {billee.name} has no KRA PIN, so this invoice isn't sent to KRA on its own. Add it to the {who}'s account, then
        send it.
      </p>
      <Input
        label="KRA PIN"
        description={`Saved on ${billee.name}'s account, for their next invoices too. A letter, 9 digits and a letter.`}
        placeholder="A123456789B"
        value={pin}
        onChange={(e) => setPin(e.target.value)}
        error={typed && !KRA_PIN.test(typed) ? "A letter, 9 digits and a letter, e.g. A123456789B" : undefined}
      />
      <div>
        <Button size="sm" disabled={!KRA_PIN.test(typed)} loading={save.isPending} onClick={() => save.mutate()}>
          Save the KRA PIN
        </Button>
      </div>
      {save.error ? (
        <Alert tone="danger" title="Not saved">
          {messageFor(errorOf(save.error))}
        </Alert>
      ) : null}
    </div>
  );
}

/** Where a submission is while KRA hasn't confirmed it. */
const TAX_STEP: Record<string, string> = {
  submitting: "Being sent to KRA.",
  submitted: "Sent; waiting for KRA's receipt.",
  uncertain: "Sent, but KRA didn't answer. Resubmit: DigiTax never records it twice.",
};

/** KRA's receipt: what proves the sale was fiscalised (the number, the device, the QR link). */
export function TaxReceipt({ receipt }: { receipt: Record<string, unknown> }) {
  const text = (k: string) =>
    typeof receipt[k] === "string" || typeof receipt[k] === "number" ? String(receipt[k]) : null;
  const qr = text("offline_url");
  return (
    <DetailList
      items={[
        { label: "KRA invoice number", value: text("invoice_number") },
        { label: "Control unit serial", value: text("serial_number") },
        { label: "Signature", value: text("receipt_signature") },
        {
          label: "QR link",
          value: qr ? (
            <a href={qr} target="_blank" rel="noreferrer" className={styles.link}>
              Open on KRA's site
            </a>
          ) : null,
        },
      ]}
    />
  );
}

/** The PDF: a preview on the page, or a download. A draft has none yet. */
function Document({ invoice: i }: { invoice: Invoice }) {
  const format = useCompanyFormat();
  const [preview, setPreview] = useState(false);
  if (i.status === "draft") return null;
  return (
    <section className={styles.section}>
      <div className={styles.cardHead}>
        <h2 className={styles.sectionTitle}>Document</h2>
        <div className={styles.actionsStart}>
          <TextButton onClick={() => setPreview((p) => !p)}>{preview ? "Hide the preview" : "Preview"}</TextButton>
          <a className={styles.link} href={pdfUrl(i.id)} download={`${i.invoice_number ?? "invoice"}.pdf`}>
            Download PDF
          </a>
        </div>
      </div>
      {i.sent_at ? (
        <p className={styles.muted}>
          Sent {formatDateTime(i.sent_at, format)}
          {i.sent_to ? ` to ${i.sent_to}` : ""}
        </p>
      ) : (
        <p className={styles.muted}>Not sent yet.</p>
      )}
      {preview ? <iframe className={styles.preview} src={pdfUrl(i.id)} title={`${i.invoice_number} PDF`} /> : null}
    </section>
  );
}

/** By SMS (with the pay link) and email (with the PDF), to the billee unless told otherwise. */
function SendDialog({ invoice, onClose }: { invoice: Invoice; onClose: () => void }) {
  const refresh = useRefreshInvoice(invoice.id);
  const [phone, setPhone] = useState("");
  const [email, setEmail] = useState("");
  const [bySms, setBySms] = useState(true);
  const [byEmail, setByEmail] = useState(true);
  const send = useMutation({
    // null skips a channel; undefined uses the billee's own number or address
    mutationFn: () =>
      invoiceActions.send(invoice.id, {
        phone_number: bySms ? phone.trim() || undefined : null,
        email: byEmail ? email.trim() || undefined : null,
      }),
    onSuccess: refresh,
  });
  const result = send.data;
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent
        title={`Send ${invoice.invoice_number}`}
        description={`By SMS with the pay link, and by email with the PDF. Leave a field empty to use ${invoice.billee.name}'s.`}
      >
        <div className={styles.form}>
          <div className={styles.pair}>
            <Checkbox
              label="By SMS"
              description="With the pay link"
              checked={bySms}
              onCheckedChange={(on) => setBySms(on === true)}
            />
            <Checkbox
              label="By email"
              description="With the PDF"
              checked={byEmail}
              onCheckedChange={(on) => setByEmail(on === true)}
            />
          </div>
          <div className={styles.pair}>
            <Input
              label="Phone (optional)"
              disabled={!bySms}
              inputMode="tel"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              placeholder="Their number"
            />
            <Input
              label="Email (optional)"
              disabled={!byEmail}
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="Their email"
            />
          </div>
          {result ? (
            <Alert
              tone={result.failed.length ? "warning" : "success"}
              title={result.failed.length ? "Partly sent" : "Sent"}
            >
              {[
                ...result.sent.map((s) => `${s.channel === "sms" ? "SMS" : "Email"} to ${s.to}`),
                ...result.failed.map((f) => `${f.channel === "sms" ? "SMS" : "Email"} to ${f.to} failed: ${f.error}`),
              ].join(". ")}
            </Alert>
          ) : null}
          {send.error ? (
            <Alert tone="danger" title="Not sent">
              {messageFor(errorOf(send.error))}
            </Alert>
          ) : null}
          <div className={styles.footer}>
            <Button variant="secondary" onClick={onClose}>
              {result ? "Close" : "Cancel"}
            </Button>
            {result ? null : (
              <Button disabled={!bySms && !byEmail} loading={send.isPending} onClick={() => send.mutate()}>
                Send
              </Button>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

/**
 * Void: irreversible, so a reason first, then a press and hold. It reverses a manual invoice's
 * posting; a paid invoice is refunded instead (`INVOICE_PAID`).
 */
export function VoidInvoice({ invoiceId }: { invoiceId: string }) {
  const refresh = useRefreshInvoice(invoiceId);
  const [reason, setReason] = useState("");
  const [confirmed, setConfirmed] = useState<boolean | undefined>(undefined);
  const voiding = useMutation({
    mutationFn: () => invoiceActions.void(invoiceId, reason.trim()),
    onSuccess: refresh,
    onError: () => setConfirmed(false),
  });
  return (
    <section className={styles.card} aria-label="Void">
      <h2 className={styles.sectionTitle}>Void</h2>
      <p className={styles.muted}>Voiding can't be undone. The customer no longer owes it, and it can't be paid.</p>
      <Textarea label="Reason" value={reason} onChange={(e) => setReason(e.target.value)} rows={2} />
      <HoldToConfirm
        label="Hold to void"
        confirmedLabel="Void"
        icon={<Ban size={16} strokeWidth={1.75} />}
        duration={VOID_HOLD_MS}
        disabled={!reason.trim() || voiding.isPending}
        confirmed={confirmed}
        onConfirm={() => {
          setConfirmed(undefined);
          voiding.mutate();
        }}
      />
      {voiding.error ? (
        <Alert tone="danger" title="Not voided">
          {messageFor(errorOf(voiding.error))}
        </Alert>
      ) : null}
    </section>
  );
}
